/**
 * แถบกระบวนท่า (the skill bar): eight slots the player fills with any move of any martial art they have learned,
 * and the rule for mixing sets (ชุดวิชา, a martial art's moves) on it.
 *
 * - **The bar.** `progress.skillBar` holds the eight slots (a move id, or null for an empty slot). Until the player
 *   arranges it (no `skillBar` saved), the bar is the active art's moves in order, so it follows `setArt` as it
 *   always did. `barSet` puts a move in a slot (null clears it; a move already on the bar moves there, the slot's
 *   old move taking its place), `barSwap` swaps two slots. Only moves of learned arts go on it.
 * - **Mixing sets (สลับชุดวิชา).** A move of one set, once started (`castStart`) or loosed (`skill`), holds every
 *   move of the other sets on the bar for `SKILL_BAR.setSwitch` seconds: their cooldown is raised to at least that.
 *   Moves of the same set chain freely; switching sets costs a 3 s wait. A bar of one set never waits.
 * - `skill` (`{ slot, at? }`) uses the move in that bar slot. The World's auto-battle uses the bar's moves too
 *   (`World.activeSkills`).
 */
import { moveLocked } from '../data/moveUnlocks';
import { artOfSkill, getArt, getSkill, SKILLS, type SkillDef } from '../data/skills';
import type { PlayerProgress } from '../progress';
import type { Point } from '../map';
import type { WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

export const SKILL_BAR = {
  /** Slots on the bar (keys 1–8). */
  slots: 8,
  /** Seconds the other sets' moves wait after a move of one set. */
  setSwitch: 3,
} as const;

/** Commands (part of `PlayerCommand`). */
export type SkillBarCommand =
  /** Put a learned move in a bar slot, or clear the slot (null). */
  | { t: 'barSet'; slot: number; skill: string | null }
  /** Swap two bar slots. */
  | { t: 'barSwap'; a: number; b: number };

type BarProgress = Pick<PlayerProgress, 'arts' | 'activeArt'> & {
  skillBar?: (string | null)[];
  movesLearned?: string[] | undefined;
};

/** Can this move go on the bar: a move of a martial art the player has learned. */
export const barSkillOk = (pr: Pick<PlayerProgress, 'arts'>, id: string): boolean => {
  const art = SKILLS[id] ? artOfSkill(id) : undefined;
  return !!art && pr.arts.includes(art.id);
};

/** The bar's eight slots: the saved arrangement, or the active art's moves until the player arranges it. */
export function skillBarOf(pr: BarProgress): (string | null)[] {
  // Unarranged: the art's moves in order, those not learned yet (systems/moveLocks.ts) after them, so the bar's first
  // slots are always the usable ones (the HUD shows the usable moves on keys 1, 2, … in this order).
  const art = getArt(pr.activeArt).skills;
  const src = pr.skillBar ?? (pr.movesLearned ? [...art.filter((id) => !moveLocked(pr, id)), ...art.filter((id) => moveLocked(pr, id))] : art);
  return Array.from({ length: SKILL_BAR.slots }, (_, i) => src[i] ?? null);
}

/** The moves on the bar, in slot order (empty slots and moves not yet learned, systems/moveLocks.ts, left out). */
export const skillBarSkills = (pr: BarProgress): SkillDef[] =>
  skillBarOf(pr).flatMap((id) => (id && barSkillOk(pr, id) && !moveLocked(pr, id) ? [getSkill(id)] : []));

/** A saved bar, checked on load: eight slots of known move ids or null; anything else is dropped. */
export function sanitizeSkillBar(v: unknown): (string | null)[] | undefined {
  if (!Array.isArray(v)) return undefined;
  return Array.from({ length: SKILL_BAR.slots }, (_, i) => {
    const id: unknown = v[i];
    return typeof id === 'string' && SKILLS[id] ? id : null;
  });
}

/** The set (martial art id) a move belongs to, or null for moves of no art. */
export const setOfSkill = (id: string): string | null => artOfSkill(id)?.id ?? null;

const slotOk = (n: unknown): n is number =>
  Number.isInteger(n) && (n as number) >= 0 && (n as number) < SKILL_BAR.slots;

export class SkillBarSystem implements WorldSystem {
  readonly name = 'skillBar';

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    skill: (c: { slot: number; at?: Point }) => this.use(c.slot, c.at),
    barSet: (c: { slot: number; skill: string | null }) => this.set(c.slot, c.skill),
    barSwap: (c: { a: number; b: number }) => this.swap(c.a, c.b),
  };

  /** The move in a bar slot, if any. */
  private use(slot: number, at?: Point): void {
    const w = this.h.world;
    if (!slotOk(slot)) return;
    const id = skillBarOf(w.progress)[slot];
    // Started (even at nobody: a whiff emits no move event), the other sets wait.
    if (id && barSkillOk(w.progress, id) && w.useSkill(w.player, id, undefined, at)) this.hold(id);
  }

  private set(slot: number, skill: string | null): void {
    const pr = this.h.world.progress as PlayerProgress;
    if (!slotOk(slot) || (skill !== null && !barSkillOk(pr, skill))) return;
    const bar = skillBarOf(pr);
    const from = skill === null ? -1 : bar.indexOf(skill);
    if (from >= 0) bar[from] = bar[slot] ?? null;
    bar[slot] = skill;
    this.save(bar);
  }

  private swap(a: number, b: number): void {
    const pr = this.h.world.progress as PlayerProgress;
    if (!slotOk(a) || !slotOk(b) || a === b) return;
    const bar = skillBarOf(pr);
    [bar[a], bar[b]] = [bar[b] ?? null, bar[a] ?? null];
    this.save(bar);
  }

  private save(bar: (string | null)[]): void {
    const pr = this.h.world.progress as PlayerProgress;
    if (JSON.stringify(bar) === JSON.stringify(skillBarOf(pr))) return;
    pr.skillBar = bar;
    this.h.emit({ type: 'progressChanged' });
  }

  /** A move of one set started or loosed by the player (the bar, or the auto-battle): the other sets wait. */
  observe(e: WorldEvent): void {
    if ((e.type === 'castStart' || e.type === 'skill') && e.srcId === this.h.world.player.id)
      this.hold(e.skillId);
  }

  /** Every move of another set on the bar waits `SKILL_BAR.setSwitch` seconds at least. */
  private hold(skillId: string): void {
    const w = this.h.world;
    const set = setOfSkill(skillId);
    if (!set || w.gmFlags.noCooldown) return;
    const p = w.player;
    for (const id of skillBarOf(w.progress)) {
      if (!id || setOfSkill(id) === set) continue;
      p.cooldowns[id] = Math.max(p.cooldowns[id] ?? 0, SKILL_BAR.setSwitch);
    }
  }
}
